'use client';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { map, max, min } from 'lodash';
import moment from 'moment';
import React from 'react';

import { formatCash, formatNumber } from '@/utils';

const FarmingChart: React.FC<{
  dataChart: number[][];
  showAxis?: boolean;
  displayMax?: boolean;
}> = ({ dataChart, showAxis = true, displayMax = false }) => {
  if (typeof window === 'undefined') return null;
  const xAxis = {
    visible: true,
    labels: {
      align: 'center',
      value: 0,
      style: {
        color: '#777E90',
        fontSize: '12px',
      },
      formatter() {
        return moment(this.value).format('DD/MM/YYYY');
      },
      maxStaggerLines: 1,
      x: 20,
    },
    minPadding: 0,
    maxPadding: 0,
    tickPosition: 'outside',
    tickColor: 'transaprent',
    endOnTick: false,
    tickPixelInterval: 0,
    lineColor: 'none',
  };
  const yAxis = {
    visible: true,
    labels: {
      enabled: true,
      align: 'left',
      x: 0,
      y: 0,
      style: {
        color: '#777E90',
        fontSize: '13px',
      },
      value: 0,
      formatter() {
        return `$${formatCash(this.value)}`;
      },
    },
    gridLineColor: 'rgba(0,0,0,0.1)',
    gridLineDashStyle: 'Dash',
    title: {
      text: '',
    },
    tickAmount: 5,
    tickPosition: 'inside',
  };

  const series = {
    color: '#27AE60',
  };

  const defaultOptions = {
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    chart: {
      type: 'area',
      height: '300px',
      width: null,
      spacing: [20, 20, 0, 20],
      renderTo: 'container',
      backgroundColor: 'transparent',
    },
    plotOptions: {
      series: {
        states: {
          hover: {
            enabled: true,
            lineWidthPlus: 0,
            halo: {
              size: 0,
              opacity: 0,
            },
          },
          select: {
            enabled: false,
          },
        },
        plotOptions: {
          series: {
            states: {
              hover: {
                enabled: true,
                lineWidthPlus: 0,
                halo: {
                  size: 0,
                  opacity: 0,
                },
              },
              select: {
                enabled: false,
              },
            },
            allowPointSelect: false,
            marker: {
              enabled: false,
            },
          },
        },
        allowPointSelect: false,
        marker: {
          enabled: false,
        },
      },
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, Highcharts?.color('#27AE60')?.setOpacity(0.2)?.get('rgba')],
            [1, Highcharts?.color('#27AE60')?.setOpacity(0)?.get('rgba')],
          ],
        },
        marker: {
          radius: 2,
        },
        lineWidth: 1,
        states: {
          hover: {
            lineWidth: 1,
          },
        },
        threshold: null,
      },
    },
    tooltip: {
      enabled: true,
      backgroundColor: '#55a173',
      borderColor: '#27AE60',
      style: {
        color: '#ffffff',
      },
      borderRadius: 10,
      borderWidth: 1,
      headerShape: 'callout',
      shadow: true,
      y: 0,
      x: 0,
      formatter() {
        return `<div style='text-align: center'>
				<div style='font-size: 15px; font-weight: 500; font-stretch: normal; font-style: normal; line-height: 1.27; letter-spacing: normal; text-align: center; color: #F6F7FB'>
					$${this.y > 999 ? formatNumber(this.y, 0, 3) : formatNumber(this.y, 2)}
				</div>
				<br />
				<div style='font-size: 11px; font-weight: normal; font-stretch: normal; font-style: normal; line-height: 1.8; letter-spacing: normal; text-align: center; color: #F6F7FB'>
					${moment(this.x).format('DD/MM/YYYY')}
				</div>
				</div>
				`;
      },
      useHTML: false,
    },
  };

  const graphOptions = () => {
    if (dataChart.length === 0) {
      return {
        series: [
          {
            data: dataChart,
          },
        ],
        yAxis: [
          {
            visible: showAxis,
          },
        ],
        xAxis: [
          {
            visible: showAxis,
          },
        ],
        lang: {
          noData: 'Not enough data',
        },
        noData: {
          style: {
            fontWeight: 'bold',
            fontSize: '15px',
            color: '#333',
          },
        },
        ...defaultOptions,
      };
    }
    const [xMax, xMin, yMax, yMin] = [
      dataChart[dataChart.length - 1]?.[0],
      dataChart[0]?.[0],
      max(map(dataChart, (v) => v[1])),
      min(map(dataChart, (v) => v[1])),
    ];
    const size = dataChart.length - 1;
    const indexes = [
      0,
      Math.floor(size / 6) + 1,
      Math.floor((size * 2) / 6) + 1,
      Math.floor((size * 3) / 6) + 1,
      Math.floor((size * 4) / 6) + 1,
      Math.floor((size * 5) / 6) + 1,
      size,
    ];
    const tickPositions = [...map(indexes, (idx) => dataChart[idx]?.[0])];
    return {
      series: [
        {
          ...series,
          data: dataChart,
        },
      ],
      yAxis: [
        {
          ...yAxis,
          visible: showAxis,
          max: displayMax
            ? (yMax || 0) + 0.00000000001
            : (yMax || 0) + ((yMax || 0) + (yMin || 0)) / 200,
          min: displayMax
            ? (yMin || 0) - 0.00000000001
            : (yMin || 0) - ((yMax || 0) + (yMin || 0)) / 200,
          tickPixelInterval: 100,
          tickAmount: displayMax ? 100 : 4,
        },
      ],
      xAxis: [
        {
          ...xAxis,
          visible: showAxis,
          max: xMax,
          min: xMin,
          tickPositions,
        },
      ],
      ...defaultOptions,
    };
  };

  return (
    <HighchartsReact
      containerProps={{ className: 'highchart' }}
      highcharts={Highcharts}
      options={graphOptions()}
    />
  );
};

export default FarmingChart;
